import React, { useState } from 'react';

const MECHANICAL_INFO_COLUMNS = [
  { dbField: 'equipment_tag_or_code', header: 'Equipment Tag/Code', isDefault: true },
  { dbField: 'description', header: 'Description', isDefault: true },
  { dbField: 'area', header: 'Area', isDefault: true },
  { dbField: 'drawing_number', header: 'Drawing Number', isDefault: true },
  { dbField: 'equipment_type', header: 'Type', isDefault: true },
  { dbField: 'sub_area', header: 'Sub Area', isDefault: false },
  { dbField: 'elevation', header: 'Elevation', isDefault: false },
  { dbField: 'floor', header: 'Floor', isDefault: false },
  { dbField: 'building_number', header: 'Building Number', isDefault: false },
  { dbField: 'date_equipment_received', header: 'Date Received', isDefault: false },
  { dbField: 'comments', header: 'Comments', isDefault: false },
  { dbField: 'activity_id', header: 'Activity ID', isDefault: false },
  { dbField: 'activity_name', header: 'Activity Name', isDefault: false },
  { dbField: 'cost_code', header: 'Cost Code', isDefault: false },
  { dbField: 'sov_code', header: 'SOV Code', isDefault: false },
  { dbField: 'wbs_code', header: 'WBS Code', isDefault: false },
  { dbField: 'p_and_id', header: 'P&ID', isDefault: false },
  { dbField: 'budgeted_hr', header: 'Budgeted Hr', isDefault: false },
  { dbField: 'credit', header: 'Credit', isDefault: false },
  { dbField: 'earned_hr', header: 'Earned Hr', isDefault: false },
];

const DEFAULT_INFO_FIELDS = MECHANICAL_INFO_COLUMNS.filter((c) => c.isDefault).map((c) => c.dbField);
const ALL_INFO_FIELDS = MECHANICAL_INFO_COLUMNS.map((c) => c.dbField);

const ExportOptionsModal = React.memo(({ isOpen, onClose, onExport, onDownloadTemplate, isExporting, disciplineName = 'Matrix', showAllTabsOption = false, filteredCount = 0, completeCount = 0, getRowIds }) => {
  const [exportType, setExportType] = useState('frontend');
  const [rowsScope, setRowsScope] = useState('filtered');
  const [allMechanicalTabs, setAllMechanicalTabs] = useState(false);
  const [infoColumns, setInfoColumns] = useState(DEFAULT_INFO_FIELDS);

  const handleToggleInfoColumn = (field) => {
    setInfoColumns((prev) => (
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    ));
  };

  const handleSubmit = () => {
    if (exportType === 'template') {
      if (onDownloadTemplate) onDownloadTemplate();
      onClose();
    } else {
      const selected = infoColumns.length > 0 ? infoColumns : DEFAULT_INFO_FIELDS;
      const useFiltered = !allMechanicalTabs && rowsScope === 'filtered';
      const rowIds = useFiltered && typeof getRowIds === 'function' ? getRowIds() : null;
      onExport(exportType, showAllTabsOption && allMechanicalTabs, selected, {
        rows: useFiltered ? 'filtered' : 'all',
        rowIds: Array.isArray(rowIds) && rowIds.length ? rowIds : undefined,
      });
    }
  };

  if (!isOpen) return null;

  const options = [
    {
      value: 'frontend',
      title: 'Dashboard Columns Only',
      description: 'Export only the columns visible in the dashboard (excludes budget, earned hours, timestamps, etc.)'
    },
    {
      value: 'all-editable',
      title: 'All Columns (Except Calculated)',
      description: 'Export all editable columns from the database, excluding auto-calculated fields like Earned Hours, Total Completion, etc.'
    },
    {
      value: 'all',
      title: 'All Columns',
      description: 'Export all columns including calculated fields (Earned Hours, Budget Cost, timestamps, etc.)'
    },
    ...(onDownloadTemplate ? [{
      value: 'template',
      title: 'Blank Template',
      description: 'Download an empty Excel template with all column headers — use this to prepare data for bulk import.'
    }] : []),
  ];

  const showInfoPicker = showAllTabsOption && allMechanicalTabs && exportType !== 'template';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        width: '90%',
        maxWidth: showInfoPicker ? '640px' : '550px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>
            📊 Export {disciplineName} Data
          </h3>
          <button
            onClick={onClose}
            disabled={isExporting}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              color: '#666',
              padding: '0.25rem',
              opacity: isExporting ? 0.5 : 1
            }}
          >
            ×
          </button>
        </div>

        <div style={{
          backgroundColor: '#e3f2fd',
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1.5rem',
          fontSize: '0.9rem',
          color: '#1976d2'
        }}>
          <strong>Export Options:</strong>
          <p style={{ margin: '0.5rem 0 0 0' }}>
            {showInfoPicker
              ? 'Default is five information columns plus each tab’s Rules of Credit. Check extra fields, or use All information columns.'
              : 'Choose which rows and columns to include in your Excel export.'}
          </p>
        </div>

        {!allMechanicalTabs && exportType !== 'template' && (
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Rows</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.7rem 0.85rem', border: `2px solid ${rowsScope === 'filtered' ? '#007bff' : '#dee2e6'}`, borderRadius: 4, marginBottom: '0.5rem', cursor: 'pointer', backgroundColor: rowsScope === 'filtered' ? '#e7f3ff' : 'white' }}>
              <input type="radio" name="trackerExportRows" checked={rowsScope === 'filtered'} onChange={() => setRowsScope('filtered')} style={{ margin: 0, width: 18, height: 18 }} />
              <div>
                <div style={{ fontWeight: 'bold', color: '#333' }}>Filtered — as displayed ({filteredCount})</div>
                <div style={{ fontSize: '0.82rem', color: '#666' }}>Only the rows currently shown after search and filters</div>
              </div>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.7rem 0.85rem', border: `2px solid ${rowsScope === 'all' ? '#007bff' : '#dee2e6'}`, borderRadius: 4, cursor: 'pointer', backgroundColor: rowsScope === 'all' ? '#e7f3ff' : 'white' }}>
              <input type="radio" name="trackerExportRows" checked={rowsScope === 'all'} onChange={() => setRowsScope('all')} style={{ margin: 0, width: 18, height: 18 }} />
              <div>
                <div style={{ fontWeight: 'bold', color: '#333' }}>Complete table ({completeCount})</div>
                <div style={{ fontSize: '0.82rem', color: '#666' }}>Every row in this tracker, ignoring the current filter</div>
              </div>
            </label>
          </div>
        )}

        {!showInfoPicker && (
          <div style={{ marginBottom: '1.5rem' }}>
            {options.map((option) => (
              <label
                key={option.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '1rem',
                  border: `2px solid ${exportType === option.value ? '#007bff' : '#dee2e6'}`,
                  borderRadius: '4px',
                  marginBottom: '0.75rem',
                  cursor: 'pointer',
                  backgroundColor: exportType === option.value ? '#e7f3ff' : 'white',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (exportType !== option.value) {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (exportType !== option.value) {
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                <input
                  type="radio"
                  name="exportType"
                  value={option.value}
                  checked={exportType === option.value}
                  onChange={(e) => setExportType(e.target.value)}
                  style={{ marginRight: '1rem', cursor: 'pointer', width: '18px', height: '18px' }}
                />
                <div>
                  <div style={{ fontWeight: 'bold', marginBottom: '0.25rem', color: '#333' }}>
                    {option.title}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>
                    {option.description}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}

        {showAllTabsOption && (
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem',
              padding: '1rem',
              border: `2px solid ${allMechanicalTabs ? '#28a745' : '#dee2e6'}`,
              borderRadius: '4px',
              marginBottom: showInfoPicker ? '1rem' : '1.5rem',
              cursor: exportType === 'template' ? 'not-allowed' : 'pointer',
              backgroundColor: allMechanicalTabs ? '#eafaf0' : 'white',
              opacity: exportType === 'template' ? 0.5 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            <input
              type="checkbox"
              checked={allMechanicalTabs}
              disabled={exportType === 'template'}
              onChange={(e) => {
                setAllMechanicalTabs(e.target.checked);
                if (e.target.checked) setInfoColumns(DEFAULT_INFO_FIELDS);
              }}
              style={{ marginTop: '0.15rem', cursor: 'pointer', width: '18px', height: '18px' }}
            />
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: '0.25rem', color: '#333' }}>
                Export all mechanical tabs into one file
              </div>
              <div style={{ fontSize: '0.85rem', color: '#666' }}>
                One workbook with a sheet per sub-table: Rotating Equipment, Vessels, Chutes, Conveyors, and Skids. ID is always the first column and Type is filled for re-import. Rules of Credit steps are always included for that tab.
              </div>
            </div>
          </label>
        )}

        {showInfoPicker && (
          <div style={{
            border: '1px solid #dee2e6',
            borderRadius: '6px',
            padding: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 600, color: '#333' }}>Information columns</div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setInfoColumns(DEFAULT_INFO_FIELDS)}
                  style={{ padding: '0.35rem 0.7rem', fontSize: '0.8rem', border: '1px solid #28a745', background: infoColumns.length === DEFAULT_INFO_FIELDS.length && DEFAULT_INFO_FIELDS.every((f) => infoColumns.includes(f)) ? '#28a745' : 'white', color: infoColumns.length === DEFAULT_INFO_FIELDS.length && DEFAULT_INFO_FIELDS.every((f) => infoColumns.includes(f)) ? 'white' : '#28a745', borderRadius: '4px', cursor: 'pointer' }}
                >
                  Default 5
                </button>
                <button
                  type="button"
                  onClick={() => setInfoColumns(ALL_INFO_FIELDS)}
                  style={{ padding: '0.35rem 0.7rem', fontSize: '0.8rem', border: '1px solid #0d6efd', background: infoColumns.length === ALL_INFO_FIELDS.length ? '#0d6efd' : 'white', color: infoColumns.length === ALL_INFO_FIELDS.length ? 'white' : '#0d6efd', borderRadius: '4px', cursor: 'pointer' }}
                >
                  All information columns
                </button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.35rem 1rem' }}>
              {MECHANICAL_INFO_COLUMNS.map((col) => (
                <label key={col.dbField} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.88rem', color: '#333', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={infoColumns.includes(col.dbField)}
                    onChange={() => handleToggleInfoColumn(col.dbField)}
                  />
                  {col.header}
                  {col.isDefault ? <span style={{ fontSize: '0.7rem', color: '#6c757d' }}>(default)</span> : null}
                </label>
              ))}
            </div>
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            disabled={isExporting}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              opacity: isExporting ? 0.5 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isExporting}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: isExporting ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {isExporting ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #fff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Exporting...
              </>
            ) : exportType === 'template' ? (
              <>📄 Download Template</>
            ) : (
              <>📊 Export to Excel</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
});

export default ExportOptionsModal;
